import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  deleteDoc,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { TableArea } from '../../types'
import { dispatchCatalogEvent, FLOOR_UPDATED_EVENT } from '../catalogEvents'
import { getFirestoreInstance } from '../firebase'
import { getCurrentPlaceId } from '../placeConfig'
import { sortTableAreasByIndex } from '../menuSortOrder'
import { commitWriteOps, type BatchWriteOp } from './writeBatches'

const UNASSIGNED_TABLE_AREA_ID = '__unassigned__'
const UNASSIGNED_TABLE_AREA_NAME = 'Unassigned'
const UNASSIGNED_TABLE_AREA_COLOR = '#94a3b8'

const unassignedTableArea: TableArea = {
  id: UNASSIGNED_TABLE_AREA_ID,
  name: UNASSIGNED_TABLE_AREA_NAME,
  color: UNASSIGNED_TABLE_AREA_COLOR,
  sortIndex: 0,
}

const tableAreasCacheByPlace: Record<string, TableArea[]> = {}
let tableAreasUnsubscribe: (() => void) | null = null

function dispatchFloorUpdated(): void {
  dispatchCatalogEvent(FLOOR_UPDATED_EVENT)
}

function tableAreasRef(placeId: string) {
  return collection(getFirestoreInstance(), 'places', placeId, 'tableAreas')
}

/** Firestore reserves doc IDs starting with __; unassigned stored as `unassigned`. */
function tableAreaFirestoreDocId(areaId: string): string {
  return areaId === UNASSIGNED_TABLE_AREA_ID ? 'unassigned' : areaId
}

function tableAreaDoc(placeId: string, areaId: string) {
  return doc(
    getFirestoreInstance(),
    'places',
    placeId,
    'tableAreas',
    tableAreaFirestoreDocId(areaId),
  )
}

function docToTableArea(d: QueryDocumentSnapshot): TableArea {
  const data = d.data()
  const id = d.id === 'unassigned' ? UNASSIGNED_TABLE_AREA_ID : d.id
  const sortIndex =
    typeof data.sortIndex === 'number' && Number.isFinite(data.sortIndex)
      ? data.sortIndex
      : undefined
  const color =
    typeof data.color === 'string' && data.color.trim()
      ? data.color.trim()
      : id === UNASSIGNED_TABLE_AREA_ID
        ? UNASSIGNED_TABLE_AREA_COLOR
        : '#6366f1'
  return {
    id,
    name: (data.name as string) ?? (id === UNASSIGNED_TABLE_AREA_ID ? UNASSIGNED_TABLE_AREA_NAME : ''),
    color,
    sortIndex,
  }
}

function ensureUnassignedInList(list: TableArea[]): TableArea[] {
  const has = list.some((a) => a.id === UNASSIGNED_TABLE_AREA_ID)
  if (has) return sortTableAreasByIndex(list)
  return sortTableAreasByIndex([unassignedTableArea, ...list])
}

export async function primeTableAreasCache(): Promise<void> {
  const placeId = getCurrentPlaceId()
  if (tableAreasUnsubscribe) {
    tableAreasUnsubscribe()
    tableAreasUnsubscribe = null
  }
  const snap = await getDocs(tableAreasRef(placeId))
  let list = snap.docs.map((d) => docToTableArea(d))
  const hasUnassigned = list.some((a) => a.id === UNASSIGNED_TABLE_AREA_ID)
  if (!hasUnassigned) {
    list = [unassignedTableArea, ...list]
    await setDoc(tableAreaDoc(placeId, UNASSIGNED_TABLE_AREA_ID), {
      id: UNASSIGNED_TABLE_AREA_ID,
      name: UNASSIGNED_TABLE_AREA_NAME,
      color: UNASSIGNED_TABLE_AREA_COLOR,
      sortIndex: 0,
    })
  }
  tableAreasCacheByPlace[placeId] = sortTableAreasByIndex(list)
  tableAreasUnsubscribe = onSnapshot(tableAreasRef(placeId), (snapshot) => {
    tableAreasCacheByPlace[placeId] = sortTableAreasByIndex(
      snapshot.docs.map((d) => docToTableArea(d)),
    )
    dispatchFloorUpdated()
  })
}

export function getTableAreas(): TableArea[] {
  const placeId = getCurrentPlaceId()
  return tableAreasCacheByPlace[placeId] ?? []
}

export function saveTableAreas(areas: TableArea[]): void {
  const placeId = getCurrentPlaceId()
  const withIndex = areas.map((a, i) => ({ ...a, sortIndex: i }))
  tableAreasCacheByPlace[placeId] = withIndex
  dispatchFloorUpdated()
  ;(async () => {
    const snap = await getDocs(tableAreasRef(placeId))
    const logicalIds = new Set(withIndex.map((a) => a.id))
    const ops: BatchWriteOp[] = []
    for (const d of snap.docs) {
      const logicalId = d.id === 'unassigned' ? UNASSIGNED_TABLE_AREA_ID : d.id
      if (!logicalIds.has(logicalId)) ops.push({ type: 'delete', ref: d.ref })
    }
    for (const a of withIndex) {
      ops.push({
        type: 'set',
        ref: tableAreaDoc(placeId, a.id),
        data: {
          id: a.id,
          name: a.name,
          color: a.color,
          sortIndex: a.sortIndex ?? 0,
        },
      })
    }
    await commitWriteOps(ops)
  })().catch((e) => console.error('Firestore saveTableAreas failed', e))
}

function generateTableAreaId(): string {
  return `TA-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function addTableArea(area: Omit<TableArea, 'id'>): TableArea {
  const placeId = getCurrentPlaceId()
  const id = generateTableAreaId()
  const cache = ensureUnassignedInList(tableAreasCacheByPlace[placeId] ?? [])
  const maxIdx = cache.reduce((m, a) => Math.max(m, a.sortIndex ?? 0), -1)
  const full: TableArea = {
    ...area,
    id,
    color: area.color?.trim() || '#6366f1',
    sortIndex: maxIdx + 1,
  }
  tableAreasCacheByPlace[placeId] = [...cache, full]
  dispatchFloorUpdated()
  setDoc(tableAreaDoc(placeId, id), {
    id: full.id,
    name: full.name,
    color: full.color,
    sortIndex: full.sortIndex ?? 0,
  }).catch((e) => console.error('Firestore addTableArea failed', e))
  return full
}

export function updateTableArea(id: string, patch: Partial<TableArea>): TableArea[] {
  if (id === UNASSIGNED_TABLE_AREA_ID) {
    const placeId = getCurrentPlaceId()
    return tableAreasCacheByPlace[placeId] ?? []
  }
  const placeId = getCurrentPlaceId()
  const cache = tableAreasCacheByPlace[placeId] ?? []
  tableAreasCacheByPlace[placeId] = cache.map((a) =>
    a.id === id ? { ...a, ...patch, color: patch.color?.trim() || a.color } : a,
  )
  const found = tableAreasCacheByPlace[placeId].find((a) => a.id === id)
  if (found) {
    setDoc(tableAreaDoc(placeId, id), {
      id: found.id,
      name: found.name,
      color: found.color,
      sortIndex: found.sortIndex ?? 0,
    }).catch((e) => console.error('Firestore updateTableArea failed', e))
  }
  dispatchFloorUpdated()
  return tableAreasCacheByPlace[placeId]
}

export function deleteTableArea(id: string): TableArea[] {
  if (id === UNASSIGNED_TABLE_AREA_ID) {
    const placeId = getCurrentPlaceId()
    return tableAreasCacheByPlace[placeId] ?? []
  }
  const placeId = getCurrentPlaceId()
  const cache = tableAreasCacheByPlace[placeId] ?? []
  tableAreasCacheByPlace[placeId] = cache.filter((a) => a.id !== id)
  deleteDoc(tableAreaDoc(placeId, id)).catch((e) =>
    console.error('Firestore deleteTableArea failed', e),
  )
  dispatchFloorUpdated()
  return tableAreasCacheByPlace[placeId]
}

export function reorderTableAreas(orderedIds: string[]): void {
  const placeId = getCurrentPlaceId()
  const cache = ensureUnassignedInList(tableAreasCacheByPlace[placeId] ?? [])
  const byId = new Map(cache.map((a) => [a.id, a]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((a): a is TableArea => a != null)
  saveTableAreas(newOrder)
}

export function isTableAreasPrimed(): boolean {
  const placeId = getCurrentPlaceId()
  return placeId in tableAreasCacheByPlace
}

export function clearTableAreasCache(): void {
  if (tableAreasUnsubscribe) {
    tableAreasUnsubscribe()
    tableAreasUnsubscribe = null
  }
  Object.keys(tableAreasCacheByPlace).forEach((k) => delete tableAreasCacheByPlace[k])
}
